import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logoImg from "../../assets/images/logo.png";

const PAGE_WIDTH_PX = 1400;
const PAGE_HEIGHT_PX = 1050;

const normalizeScript = (raw = "") =>
  String(raw)
    .replace(/\\r\\n|\\n|\\r/g, "\n")
    .replace(/\\'/g, "'")
    .replace(/\\"/g, '"')
    .trim();

const escapeHtml = (str = "") =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result || "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Prefer full-size image over thumbnail for PDF quality.
 */
const toFullSizeImageUrl = (imageUrl = "") => {
  const url = String(imageUrl || "").trim();
  if (!url) return "";
  return url.replace(/\/menu-item-images\/thumb\//g, "/menu-item-images/");
};

/**
 * Load remote image as base64 using browser only (requires CORS on image host).
 */
const resolveImageDataUrl = async (imageUrl = "") => {
  const url = toFullSizeImageUrl(imageUrl);
  if (!url) return "";
  if (url.startsWith("data:")) return url;

  // 1) fetch → blob → data URL
  try {
    const response = await fetch(url, { mode: "cors", credentials: "omit" });
    if (response.ok) {
      const blob = await response.blob();
      if (blob && blob.type.startsWith("image/")) {
        return await blobToDataUrl(blob);
      }
    }
  } catch (e) {
    // continue to Image fallback
  }

  // 2) Image + canvas (also needs CORS)
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        if (!canvas.width || !canvas.height) {
          resolve("");
          return;
        }
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.92));
      } catch (err) {
        console.warn("Image canvas convert failed (likely CORS):", url, err);
        resolve("");
      }
    };
    img.onerror = () => {
      console.warn("Image load failed (likely CORS):", url);
      resolve("");
    };
    // cache-bust helps when a non-CORS cached response exists
    const joiner = url.includes("?") ? "&" : "?";
    img.src = `${url}${joiner}t=${Date.now()}`;
  });
};

// All known section-heading emojis used in coaching scripts
const SECTION_EMOJIS = [
  "🍽️", "✨", "🍷", "💬", "⚠️", "⚠", "🎯", "💡", "⭐", "🔥", "👌",
  "🍴", "🥂", "🌿", "🧄", "🫒", "🍋", "🌶️", "🧅", "🧂",
  "🥩", "🍤", "🫙", "🥗", "🍜", "🍝", "🍛", "🥘", "🧆",
  "🫕", "🎶", "🏆", "📝", "🤝", "👏", "🌟", "💎", "🥄",
  "🫧", "🍃", "🌸", "🍯", "🥞", "🧇", "🥐", "🍞", "🥖",
];

const SPACER_HTML = '<div style="height:14px;display:block;"></div>';

/**
 * Insert a blank-line spacer before every section heading in already-formatted HTML.
 * Works by splitting on known section-emoji patterns and rejoining with spacers.
 */
const injectSectionSpacing = (html = "") => {
  const emojiPattern = SECTION_EMOJIS
    .map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");

  const emojiRe = new RegExp(`(${emojiPattern})`, "gi");

  // Step 1: Flatten everything into a single string of text nodes by:
  //   - Collapsing multiple <br> into one
  //   - Collapsing </div><div> into a single <br>
  //   - Stripping extra whitespace/newlines between tags
  let flat = html
    // Merge </div> ... <div> into a single <br>
    .replace(/<\/div>\s*<div[^>]*>/gi, "<br>")
    // Remove wrapping <div> / </div>
    .replace(/<\/?div[^>]*>/gi, "")
    // Collapse 2+ <br> (with optional whitespace between) into one <br>
    .replace(/(<br\s*\/?>(\s|&nbsp;)*){2,}/gi, "<br>")
    // Trim leading/trailing whitespace and newlines around <br>
    .replace(/\s*<br\s*\/?>\s*/gi, "<br>")
    // Collapse any remaining multiple newlines / spaces into a single space
    .replace(/\n+/g, " ")
    .trim();

  // Step 2: Split on section-emoji boundaries (keep the emoji with its section)
  // We split BEFORE each emoji (except the very first)
  const parts = flat.split(emojiRe);
  // split with a capture group gives: [before_first, emoji1, content1, emoji2, content2, ...]
  // parts[0] = content before first emoji (usually empty)
  // parts[1] = emoji1, parts[2] = content after emoji1
  // parts[3] = emoji2, parts[4] = content after emoji2 ...

  if (parts.length <= 1) return html;

  // Rebuild: first section has no leading spacer, subsequent ones do
  let result = parts[0]; // content before first emoji (usually empty/whitespace)
  for (let i = 1; i < parts.length; i += 2) {
    const emoji = parts[i];
    // Strip any leading <br> so the title stays on the same line as the emoji
    const content = (parts[i + 1] || "").replace(/^(<br\s*\/?>)+/i, "");
    if (i === 1) {
      result += emoji + content;
    } else {
      result += `<br>${SPACER_HTML}` + emoji + content;
    }
  }

  return result;
};

/**
 * Convert plain or lightly-escaped coaching script into structured HTML.
 */
const formatCoachingScriptHtml = (raw = "") => {
  const text = normalizeScript(raw);
  if (!text) return "<p style='color:#888;'>No coaching script available.</p>";

  // Already HTML from AI formatting — do NOT add extra <br> on top of existing markup.
  // Inject a spacer div before each section heading so sections are visually separated.
  if (/<\/?(strong|b|br|p|ul|li|div|em)\b/i.test(text)) {
    return injectSectionSpacing(text);
  }

  const sections = [];
  const sectionMarkers = ["🍽️", "✨", "🍷", "💬", "⚠️", "⚠"];
  const markerRegex = /(🍽️|✨|🍷|💬|⚠️|⚠)\s*/g;
  const parts = [];
  let lastIndex = 0;
  let markerMatch;

  while ((markerMatch = markerRegex.exec(text)) !== null) {
    if (markerMatch.index > lastIndex) {
      // Leading text before first marker is ignored for section parsing
    }
    const emoji = markerMatch[1];
    const start = markerMatch.index + markerMatch[0].length;
    const next = sectionMarkers
      .map((m) => {
        const idx = text.indexOf(m, start);
        return idx === -1 ? Infinity : idx;
      })
      .reduce((a, b) => Math.min(a, b), Infinity);
    const end = Number.isFinite(next) ? next : text.length;
    parts.push({ emoji, body: text.slice(start, end).trim() });
    lastIndex = end;
    markerRegex.lastIndex = end;
  }

  parts.forEach(({ emoji, body }) => {
    if (!body) return;

    const bulletParts = body.split("•").map((s) => s.trim()).filter(Boolean);
    const heading = bulletParts.shift() || "";
    const bullets = bulletParts;

    sections.push(`
      <div class="fc-section">
        <div class="fc-section-title">${emoji} <strong>${escapeHtml(heading)}</strong></div>
        ${
          bullets.length
            ? `<ul class="fc-bullets">${bullets
                .map((b) => `<li>${escapeHtml(b)}</li>`)
                .join("")}</ul>`
            : ""
        }
      </div>
    `);
  });

  if (sections.length) return sections.join("");

  // Fallback: show bullets if present
  if (text.includes("•")) {
    const items = text.split("•").map((s) => s.trim()).filter(Boolean);
    return `<ul class="fc-bullets">${items
      .map((b) => `<li>${escapeHtml(b)}</li>`)
      .join("")}</ul>`;
  }

  return `<p>${escapeHtml(text)}</p>`;
};

const waitForImages = (root) => {
  const images = Array.from(root.querySelectorAll("img"));
  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    )
  );
};

const buildPageElement = (card, logoSrc, imageDataUrl = "") => {
  const title = escapeHtml(card.flash_card_title || "Flashcard");
  const scriptHtml = formatCoachingScriptHtml(card.format_coching_script || "");

  const page = document.createElement("div");
  page.className = "flashcard-pdf-page";
  page.style.cssText = `
    width: ${PAGE_WIDTH_PX}px;
    min-height: ${PAGE_HEIGHT_PX}px;
    background: #ffffff;
    box-sizing: border-box;
    padding: 36px 40px 32px;
    font-family: Arial, Helvetica, sans-serif;
    color: #222;
    display: flex;
    flex-direction: column;
    position: relative;
  `;

  page.innerHTML = `
    <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:24px; margin-bottom:28px;">
      <div style="
        flex: 1;
        background: #ececec;
        border-radius: 10px;
        padding: 16px 22px;
        font-size: 26px;
        font-weight: 700;
        line-height: 1.3;
        color: #1f1f1f;
        min-height: 58px;
        display: flex;
        align-items: center;
      ">${title}</div>
      <div style="flex-shrink:0; text-align:right; width: 260px;">
        <img
          src="${logoSrc}"
          alt="PerfectCheck"
          style="width: 240px; height: auto; display:block; margin-left:auto;"
        />
      </div>
    </div>

    <div style="flex:1; display:flex; gap:28px; align-items:flex-start;">
      <div style="
        flex: 0 0 46%;
        display:flex;
        align-items:center;
        justify-content:center;
        box-sizing: border-box;
      ">
        <div style="
          width: 560px;
          height: 560px;
          max-width: 90%;
          max-height: 85%;
          background: #f3f3f3;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
        ">
          ${
            imageDataUrl
              ? `<img
                  src="${imageDataUrl}"
                  alt=""
                  style="
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    object-position: center;
                    display: block;
                  "
                />`
              : `<span style="color:#999; font-size:18px;">No image</span>`
          }
        </div>
      </div>

      <div style="
        flex: 1;
        border: 1px solid #e5e5e5;
        border-radius: 14px;
        padding: 22px 26px;
        overflow: visible;
        background: #fff;
        box-sizing: border-box;
      ">
        <div class="fc-script-content" style="
          font-size: 16px;
          line-height: 1.4;
          color: #222;
        ">
          ${scriptHtml}
        </div>
      </div>
    </div>

    <style>
      .fc-script-content p { margin: 0 0 4px 0; }
      .fc-script-content br { line-height: 1; }
      .fc-section { margin-bottom: 10px; }
      .fc-section-title {
        font-size: 16px;
        font-weight: 700;
        margin-bottom: 4px;
        color: #111;
      }
      .fc-bullets {
        margin: 2px 0 0 0;
        padding-left: 18px;
      }
      .fc-bullets li {
        margin-bottom: 3px;
        font-size: 15px;
      }
      .fc-script-content strong,
      .fc-script-content b {
        font-weight: 700;
      }
      .fc-script-content ul {
        margin: 2px 0;
        padding-left: 18px;
      }
      .fc-script-content ul li {
        margin-bottom: 3px;
      }
    </style>
  `;

  return page;
};

// A4 landscape in mm
const A4_WIDTH_MM = 297;
const A4_HEIGHT_MM = 210;

/**
 * Build one landscape PDF with 1 flashcard per page.
 * Each page is sized to fully contain its content (no clipping).
 */
export const exportFlashCardsToPDF = async (flashCards = [], fileName = "FlashCards.pdf") => {
  if (!Array.isArray(flashCards) || flashCards.length === 0) {
    throw new Error("No flashcards available to export");
  }

  const host = document.createElement("div");
  host.style.cssText = `
    position: fixed;
    left: -10000px;
    top: 0;
    width: ${PAGE_WIDTH_PX}px;
    z-index: -1;
    pointer-events: none;
  `;
  document.body.appendChild(host);

  // Collect all rendered canvases first
  const canvases = [];
  try {
    for (const card of flashCards) {
      const imageDataUrl = await resolveImageDataUrl(card.image_name || "");
      const pageEl = buildPageElement(card, logoImg, imageDataUrl);
      host.innerHTML = "";
      host.appendChild(pageEl);
      await waitForImages(pageEl);

      const renderedHeight = Math.max(pageEl.scrollHeight, PAGE_HEIGHT_PX);

      const canvas = await html2canvas(pageEl, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        width: PAGE_WIDTH_PX,
        height: renderedHeight,
        windowWidth: PAGE_WIDTH_PX,
        windowHeight: renderedHeight,
      });

      canvases.push(canvas);
    }
  } finally {
    if (host.parentNode) host.parentNode.removeChild(host);
  }

  // Build PDF — each page sized to its canvas aspect ratio, width = A4 landscape width
  let pdf = null;
  for (let i = 0; i < canvases.length; i++) {
    const canvas = canvases[i];
    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    const aspectRatio = canvas.height / canvas.width;
    const pageHeightMm = Math.max(A4_WIDTH_MM * aspectRatio, A4_HEIGHT_MM);

    if (i === 0) {
      pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: [A4_WIDTH_MM, pageHeightMm],
      });
    } else {
      pdf.addPage([A4_WIDTH_MM, pageHeightMm], "landscape");
    }

    pdf.addImage(imgData, "JPEG", 0, 0, A4_WIDTH_MM, pageHeightMm);
  }

  pdf.save(fileName);
};

export default exportFlashCardsToPDF;
